/* The task form — four sections: what to do, where, when, and the answer form.
 *
 * A field renderer here is the CONTROL ONLY; `<Form.Item>` owns the label, the required marker and
 * the error, exactly as `points/ResourceForm.js` does it. `<Form component={false}>` because the
 * item lives in `Page.js`'s state, not antd's field store — there is nothing to submit.
 *
 * Two things this form deliberately does NOT let you edit, both because the API has no such column
 * and the old form's versions were discarded on every save:
 *
 *   - **priority** and **components** belong to the TEMPLATE (`source="template.priority"` /
 *     `"template.components"` on the read serializer). `Task` has neither. The old page offered
 *     priority as an editable select and posted it; DRF dropped it silently. They are shown here as
 *     read-only facts, which is also what makes the template select meaningful — the template is
 *     *what the merchandiser will be asked for*.
 *   - **the target** (`point` / `network` / `point_group`) after creation. `Task.point` is a CASCADE
 *     FK, so re-pointing a task that already has answers would leave them attached to a different
 *     point.
 */

import React from "react";
import { ConfigProvider, DatePicker, Flex, Form, Input, Space, Tag, Typography, Upload } from "antd";
import { ApartmentOutlined, ClusterOutlined, DeleteOutlined, PictureOutlined, PlusOutlined, PushpinOutlined } from "@ant-design/icons";

import EntitySelect from "../../component/EntitySelect";
import { TASK_DETAIL } from "../../config/lang";
import dayjs from "../../util/dayjs";
import { translate } from "../../util/util";
import { localeFor } from "../merchandiser/route/dateUtils";

/** `Template.Components` — what the merchandiser is asked to submit. */
const COMPONENT_LABEL = { photo: "cmp_photo", text: "cmp_text", boolean: "cmp_boolean", numeric: "cmp_numeric", tags: "cmp_tags" };

/** The target kinds, which are also the `/task/new/:type` route segments. A network or a group is a
    write-only FAN-OUT input: the server turns either into one task per point, which is why neither
    ever comes back on a read and why creating with one lands on the list rather than a record. */
export const TARGETS = ["point", "network", "point_group"];

function Section({ title, desc, children }) {
  return (
    <section className="dp-card">
      <div className="dp-sec-head">
        <h2 className="dp-sec-title">{title}</h2>
        {desc ? <p className="dp-sec-desc">{desc}</p> : null}
      </div>
      {children}
    </section>
  );
}

export default function TaskForm({ item, target, isNew, readOnly, errors = {}, lang, onChange, onImage, onImageRemove }) {
  const t = key => translate(TASK_DETAIL, key, lang);
  /* Formatted via Intl with the active locale, never hand-built (spec/04). */
  const dateText = value => new Intl.DateTimeFormat(localeFor(lang), { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value));

  const err = name => (errors[name] ? { validateStatus: "error", help: errors[name] } : {});

  /**
   * One field.
   *
   * ⚠ In VIEW mode this renders the VALUE AS TEXT, not the control with `disabled`. antd draws a
   * disabled input's text at `rgba(23,28,32,.25)` on a `.04` fill — measured at roughly 1.9:1,
   * against a 4.5:1 floor — so the first version of this page was literally unreadable in the state
   * it opens in. Nothing is gained by showing an inert input either: view mode should read like a
   * record, which is what spec/03's archetype B asks for and what `points/ResourceView.js` does for
   * the descriptor resources.
   *
   * `view` is the text to show; pass `view: null` for a field that renders its own display (the
   * image), and it falls through to `node`.
   */
  const field = (name, node, extra = {}) => {
    const readable = readOnly && extra.view !== undefined;
    return (
      <div data-field={name}>
        <Form.Item label={t(extra.label || `f_${name}`)} required={!readOnly && extra.required} extra={extra.hint} {...(readOnly ? {} : err(name))}>
          {readable ? <div className="tk-value">{extra.view || <span className="tk-value--empty">—</span>}</div> : node}
        </Form.Item>
      </div>
    );
  };

  const image = item.images?.[0];
  const imageUrl = image?.image || image?.preview || null;

  return (
    /* Large controls come from one scoped ConfigProvider, not from a `size` on each renderer —
       same as ResourceForm, so the two detail pages cannot drift apart on control height. */
    <ConfigProvider componentSize="large">
      {/* ⚠ Both classes go on the Flex (a real div): `component={false}` renders no DOM node, so a
          className on the <Form> is dropped silently — taking `dp-form`'s label weight and
          `width: 100%` with it. Verify with `document.querySelectorAll(".dp-form").length`. */}
      <Form layout="vertical" component={false} colon={false} requiredMark>
        <Flex vertical gap="middle" className="dp-form tk-form">
          <Section title={t("sec_what")} desc={t("sec_what_desc")}>
            <Flex vertical gap="middle">
              {field(
                "title",
                <Input
                  maxLength={256}
                  disabled={readOnly}
                  value={item.title || ""}
                  onChange={event => onChange("title", event.target.value)}
                  placeholder={t("f_title")}
                />,
                { required: true, view: item.title }
              )}
              {field(
                "description",
                <Input.TextArea
                  rows={3}
                  maxLength={2000}
                  disabled={readOnly}
                  value={item.description || ""}
                  onChange={event => onChange("description", event.target.value)}
                />,
                { view: item.description }
              )}
              {field(
                "image",
                <Flex vertical gap="small">
                  {imageUrl ? (
                    <img className="tk-shot" src={imageUrl} alt="" />
                  ) : (
                    <div className="tk-shot tk-shot--empty">
                      <PictureOutlined />
                    </div>
                  )}
                  {readOnly ? null : (
                    <Space size={8}>
                      {/* `beforeUpload` returns false: the file is read here and posted to
                          `images/` as a data URL, because that is the only shape that endpoint's
                          Base64ImageField accepts. antd's own uploader would PUT the raw file. */}
                      <Upload accept="image/jpeg,image/png" showUploadList={false} maxCount={1} beforeUpload={file => (onImage(file), false)}>
                        <Typography.Link>
                          <PlusOutlined /> {t(imageUrl ? "img_change" : "img_add")}
                        </Typography.Link>
                      </Upload>
                      {imageUrl ? (
                        <Typography.Link onClick={onImageRemove}>
                          <DeleteOutlined /> {t("img_remove")}
                        </Typography.Link>
                      ) : null}
                    </Space>
                  )}
                </Flex>,
                { hint: t("f_image_hint") }
              )}
            </Flex>
          </Section>

          <Section title={t("sec_where")} desc={isNew ? undefined : t("sec_where_desc")}>
            <Flex vertical gap="middle">
              {/* On an existing task the target is one read-only line: whichever of the three it was
                  created with, only `point` survives on a read. */}
              {!isNew ? (
                field("point", null, { view: [item.point?.locality?.name, item.point?.name].filter(Boolean).join(" · ") })
              ) : target === "network" ? (
                field(
                  "network",
                  <EntitySelect
                    entity="network"
                    value={item.network}
                    prefix={<ClusterOutlined />}
                    extraQuery={{ active: true }}
                    onChange={option => onChange("network", option)}
                  />,
                  { required: true }
                )
              ) : target === "point_group" ? (
                <>
                  {field(
                    "group",
                    <EntitySelect
                      entity="point_groups"
                      value={item.point_groups}
                      prefix={<ApartmentOutlined />}
                      onChange={option => onChange("point_groups", option)}
                    />,
                    { required: true }
                  )}
                  {field(
                    "group_name",
                    <Input maxLength={120} value={item.title_group_uploads || ""} onChange={event => onChange("title_group_uploads", event.target.value)} />,
                    { required: true }
                  )}
                </>
              ) : (
                field(
                  "point",
                  <EntitySelect
                    entity="point"
                    value={item.point}
                    prefix={<PushpinOutlined />}
                    extraQuery={{ active: true }}
                    onChange={option => onChange("point", option)}
                  />,
                  { required: true }
                )
              )}
            </Flex>
          </Section>

          <Section title={t("sec_when")}>
            <Flex gap="middle" wrap>
              <div className="tk-form__half">
                {field(
                  "start",
                  <DatePicker
                    disabled={readOnly}
                    value={item.start ? dayjs(item.start) : null}
                    onChange={value => onChange("start", value ? value.startOf("day").toISOString() : null)}
                  />,
                  { view: item.start ? dateText(item.start) : null }
                )}
              </div>
              <div className="tk-form__half">
                {field(
                  "end",
                  <DatePicker
                    disabled={readOnly}
                    value={item.end ? dayjs(item.end) : null}
                    /* End of day, so a deadline of "today" includes today — the same inclusivity
                       the list's period filter applies. */
                    onChange={value => onChange("end", value ? value.endOf("day").toISOString() : null)}
                  />,
                  { required: true, view: item.end ? dateText(item.end) : null }
                )}
              </div>
            </Flex>
          </Section>

          <Section title={t("sec_form")} desc={t("sec_form_desc")}>
            <Flex vertical gap="middle">
              {field("template", <EntitySelect entity="template" value={item.template} onChange={option => onChange("template", option)} />, {
                required: true,
              })}
              {/* Read-only, from the template — see the file header. */}
              <ul className="tk-facts">
                <li>
                  <span className="tk-facts__k">{t("f_priority")}</span>
                  <span className="tk-facts__v">
                    {item.priority === null || item.priority === undefined ? (
                      <Typography.Text type="secondary">{t("from_template")}</Typography.Text>
                    ) : (
                      t(`prio_${item.priority}`)
                    )}
                  </span>
                </li>
                <li>
                  <span className="tk-facts__k">{t("f_components")}</span>
                  <span className="tk-facts__v">
                    {(item.components || []).length ? (
                      <Space size={4} wrap>
                        {item.components.map(name => (
                          <Tag key={name}>{COMPONENT_LABEL[name] ? t(COMPONENT_LABEL[name]) : name}</Tag>
                        ))}
                      </Space>
                    ) : (
                      <Typography.Text type="secondary">{t("from_template")}</Typography.Text>
                    )}
                  </span>
                </li>
              </ul>
            </Flex>
          </Section>
        </Flex>
      </Form>
    </ConfigProvider>
  );
}
